import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Trash2, ChevronLeft, ChevronRight, BoxSelect, Pencil } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

// Éditeur admin des zones cliquables (encadrés dorés) des catalogues interactifs :
// déplacer / redimensionner / réassigner / ajouter / supprimer des zones sur une page.
export default function CatalogueZonesEditor() {
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [pageNums, setPageNums] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [sel, setSel] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  // Fiche du produit rattaché à la zone sélectionnée (édition complète)
  const [fiche, setFiche] = useState(null);
  const [ficheOrig, setFicheOrig] = useState(null);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [ficheSaving, setFicheSaving] = useState(false);
  // Rapport des zones dorées sans produit rattaché (surlignées rouge, admin seulement)
  const [missingReport, setMissingReport] = useState(null);
  const loadMissing = () => api.get("/admin/catalogue-pages/missing-products")
    .then((r) => setMissingReport(r.data)).catch(() => {});
  useEffect(() => { loadMissing(); }, []);

  // Activer/désactiver un catalogue sur le site (inactif = carte en popup fournisseur)
  const [disabledCats, setDisabledCats] = useState([]);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setDisabledCats(r.data?.disabled_catalogues || [])).catch(() => {});
  }, []);
  const toggleCat = async (no) => {
    const next = disabledCats.includes(no) ? disabledCats.filter((x) => x !== no) : [...disabledCats, no];
    try {
      await api.patch("/admin/settings", { disabled_catalogues: next });
      setDisabledCats(next);
      toast.success(`Catalogue nº ${no} ${next.includes(no) ? "désactivé — sa carte affiche la popup fournisseur" : "activé — visionneuse interactive en ligne"}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Résumé des catalogues au chargement
  useEffect(() => {
    api.get("/admin/catalogue-pages/summary").then((res) => {
      setCats(res.data);
      setSummaryLoaded(true);
      if (res.data.length) {
        setCat(res.data[0].catalogue_no);
        setPageNums(res.data[0].page_nums);
        setPageIdx(0);
      }
    }).catch((err) => { setSummaryLoaded(true); toast.error(formatApiError(err.response?.data?.detail)); });
  }, []);

  const loadPage = (catalogue, pageNum) => {
    if (!catalogue || pageNum == null) return;
    setLoading(true);
    setSel(null);
    setQ("");
    setResults([]);
    setFicheOpen(false);
    setFiche(null);
    api.get(`/admin/catalogue-pages/${catalogue}/${pageNum}`).then((res) => {
      setPageData(res.data);
      setHotspots((res.data.hotspots || []).map((h) => ({ ...h })));
      setProductMap(res.data.products || {});
      setDirty(false);
    }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPage(cat, pageNums[pageIdx]); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cat, pageIdx, pageNums]);

  const switchCat = (c) => {
    if (dirty && !window.confirm("Modifications non enregistrées — changer de catalogue quand même ?")) return;
    const entry = cats.find((x) => x.catalogue_no === c);
    setCat(c);
    setPageNums(entry ? entry.page_nums : []);
    setPageIdx(0);
  };

  const goPage = (idx) => {
    if (idx < 0 || idx >= pageNums.length) return;
    if (dirty && !window.confirm("Modifications non enregistrées — changer de page quand même ?")) return;
    setPageIdx(idx);
  };

  // Recherche produit (réassignation / nouvelle zone)
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/products", { params: { q: q.trim(), limit: 8 } })
        .then((res) => setResults(res.data.items || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // --- géométrie : conversion pixels souris <-> pixels page ---
  const toPage = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const s = r.width / pageData.width;
    return { x: (e.clientX - r.left) / s, y: (e.clientY - r.top) / s };
  };

  const clampHotspot = (h) => ({
    ...h,
    x: Math.round(Math.max(0, Math.min(h.x, pageData.width - h.w))),
    y: Math.round(Math.max(0, Math.min(h.y, pageData.height - h.h))),
    w: Math.round(Math.max(8, Math.min(h.w, pageData.width))),
    h: Math.round(Math.max(8, Math.min(h.h, pageData.height))),
  });

  const onHotspotDown = (e, i, handle) => {
    if (drawMode) return;
    e.preventDefault();
    e.stopPropagation();
    if (i !== sel) { setFicheOpen(false); setFiche(null); }
    setSel(i);
    dragRef.current = { type: handle ? "resize" : "move", idx: i, start: toPage(e), orig: { ...hotspots[i] } };
  };

  const onCanvasDown = (e) => {
    if (!drawMode || !pageData) { if (!drawMode) { setSel(null); setFicheOpen(false); setFiche(null); } return; }
    e.preventDefault();
    const p = toPage(e);
    const h = { x: p.x, y: p.y, w: 1, h: 1, pid: "" };
    setHotspots((hs) => [...hs, h]);
    dragRef.current = { type: "draw", idx: hotspots.length, start: p, orig: h };
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || !pageData) return;
      const p = toPage(e);
      const dx = p.x - d.start.x;
      const dy = p.y - d.start.y;
      setHotspots((hs) => hs.map((h, i) => {
        if (i !== d.idx) return h;
        if (d.type === "move") return clampHotspot({ ...h, x: d.orig.x + dx, y: d.orig.y + dy });
        // resize + draw : poignée coin inférieur droit
        return clampHotspot({ ...h, w: Math.max(8, d.orig.w + dx), h: Math.max(8, d.orig.h + dy) });
      }));
      setDirty(true);
    };
    const onUp = () => {
      if (dragRef.current?.type === "draw") {
        setSel(dragRef.current.idx);  // sélectionne la nouvelle zone pour assigner le produit
        setDrawMode(false);
      }
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const assign = (p) => {
    setHotspots((hs) => hs.map((h, i) => (i === sel ? { ...h, pid: p.id } : h)));
    setProductMap((m) => ({ ...m, [p.id]: { id: p.id, name: p.name, sku: p.sku, image: p.image } }));
    setQ("");
    setResults([]);
    setDirty(true);
  };

  const removeSel = () => {
    setHotspots((hs) => hs.filter((_, i) => i !== sel));
    setSel(null);
    setFicheOpen(false);
    setFiche(null);
    setDirty(true);
  };

  // --- fiche du produit rattaché : édition complète sans quitter l'éditeur de zones ---
  const slugify = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "item";

  const normList = (rows, kind) => {
    const seen = new Set();
    return (rows || []).filter((r) => (r.label || "").trim()).map((r, i) => {
      let id = r.id || slugify(r.label);
      while (seen.has(id)) id = `${id}-${i}`;
      seen.add(id);
      const extra = parseFloat(r.extra);
      if (!(extra >= 0)) throw new Error(`Supplément invalide — ${kind} « ${r.label} »`);
      return { id, label: r.label.trim(), extra, label_en: r.label_en || "" };
    });
  };

  const normTiers = (rows) => (rows || [])
    .filter((r) => String(r.min_qty).trim() !== "" || String(r.price).trim() !== "")
    .map((r) => {
      const min_qty = parseInt(r.min_qty);
      const price = parseFloat(r.price);
      if (!(min_qty >= 1) || !(price > 0)) throw new Error("Palier invalide (quantité ≥ 1, prix > 0)");
      return { min_qty, price };
    });

  const openFiche = async () => {
    if (!selH?.pid) return;
    if (ficheOpen) { setFicheOpen(false); return; }
    try {
      const { data } = await api.get(`/products/${selH.pid}`);
      const loaded = {
        name: data.name || "", name_en: data.name_en || "", sku: data.sku || "",
        base_price: data.base_price ?? "", cost_price: data.cost_price ?? "",
        tagline: data.tagline || "", description: data.description || "",
        description_en: data.description_en || "",
        sizes: (data.sizes || []).map((s) => ({ ...s })),
        finishes: (data.finishes || []).map((s) => ({ ...s })),
        price_tiers: (data.price_tiers || []).map((s) => ({ ...s })),
      };
      setFiche(loaded);
      setFicheOrig(loaded);
      setFicheOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updRow = (k, i, field, v) => setFiche((f) => ({ ...f, [k]: f[k].map((r, j) => (j === i ? { ...r, [field]: v } : r)) }));
  const addRow = (k, blank) => setFiche((f) => ({ ...f, [k]: [...f[k], blank] }));
  const delRow = (k, i) => setFiche((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  const saveFiche = async () => {
    if (!fiche.name.trim() || fiche.name.trim().length < 2) return toast.error("Nom trop court (2 caractères min.)");
    const price = parseFloat(fiche.base_price);
    if (!(price > 0)) return toast.error("Prix de base invalide (doit être supérieur à 0)");
    const cost = String(fiche.cost_price).trim() === "" ? null : parseFloat(fiche.cost_price);
    if (cost !== null && !(cost >= 0)) return toast.error("Prix coûtant invalide");
    let sizes, finishes, price_tiers;
    try {
      sizes = normList(fiche.sizes, "grandeur");
      finishes = normList(fiche.finishes, "finition");
      price_tiers = normTiers(fiche.price_tiers);
    } catch (e) {
      return toast.error(e.message);
    }
    // N'envoyer que les champs MODIFIÉS (PATCH partiel ; évite de rejeter les
    // descriptions importées plus longues que la limite du formulaire)
    const payload = {};
    const maybeStr = (k, val, orig) => { if (val !== orig) payload[k] = val; };
    maybeStr("name", fiche.name.trim(), ficheOrig.name);
    maybeStr("name_en", fiche.name_en.trim(), ficheOrig.name_en);
    maybeStr("sku", fiche.sku.trim(), ficheOrig.sku);
    maybeStr("tagline", fiche.tagline.trim(), ficheOrig.tagline);
    maybeStr("description", fiche.description, ficheOrig.description);
    maybeStr("description_en", fiche.description_en, ficheOrig.description_en);
    if (price !== parseFloat(ficheOrig.base_price)) payload.base_price = price;
    if (cost !== null && cost !== ficheOrig.cost_price) payload.cost_price = cost;
    if (JSON.stringify(sizes) !== JSON.stringify(normList(ficheOrig.sizes, "grandeur"))) payload.sizes = sizes;
    if (JSON.stringify(finishes) !== JSON.stringify(normList(ficheOrig.finishes, "finition"))) payload.finishes = finishes;
    if (JSON.stringify(price_tiers) !== JSON.stringify(normTiers(ficheOrig.price_tiers))) payload.price_tiers = price_tiers;
    if (!Object.keys(payload).length) { setFicheOpen(false); return toast.info("Aucune modification"); }
    setFicheSaving(true);
    try {
      await api.patch(`/admin/products/${selH.pid}`, payload);
      setProductMap((m) => ({
        ...m,
        [selH.pid]: { ...(m[selH.pid] || {}), id: selH.pid, name: payload.name ?? ficheOrig.name, sku: payload.sku ?? ficheOrig.sku },
      }));
      toast.success("Fiche produit mise à jour");
      setFicheOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setFicheSaving(false);
    }
  };

  const save = () => {
    const missing = hotspots.filter((h) => !h.pid).length;
    if (missing) { toast.error(`${missing} zone(s) sans produit — assignez un produit ou supprimez-les.`); return; }
    setSaving(true);
    api.put(`/admin/catalogue-pages/${cat}/${pageData.page}/hotspots`, { hotspots })
      .then((res) => {
        toast.success(`${res.data.hotspots} zone(s) enregistrée(s) — page ${pageData.page}`);
        setDirty(false);
        loadMissing();
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setSaving(false));
  };

  // Saut direct vers une page du rapport « zones sans produit »
  const jumpTo = (c, pageNum) => {
    if (dirty && !window.confirm("Modifications non enregistrées — changer de page quand même ?")) return;
    if (c !== cat) {
      const entry = cats.find((x) => x.catalogue_no === c);
      const nums = entry ? entry.page_nums : [];
      const idx = nums.indexOf(pageNum);
      setCat(c);
      setPageNums(nums);
      setPageIdx(idx >= 0 ? idx : 0);
    } else {
      const idx = pageNums.indexOf(pageNum);
      if (idx >= 0) setPageIdx(idx);
    }
  };

  const selH = sel != null ? hotspots[sel] : null;
  const selProd = selH?.pid ? productMap[selH.pid] : null;

  return (
    <div data-testid="zones-editor">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {cats.map((c) => {
          const disabled = disabledCats.includes(c.catalogue_no);
          return (
            <span key={c.catalogue_no} className="inline-flex items-center gap-1.5">
              <button data-testid={`zones-cat-${c.catalogue_no}`} onClick={() => switchCat(c.catalogue_no)}
                className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${cat === c.catalogue_no ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60"}`}>
                Catalogue nº {c.catalogue_no} ({c.hotspots} zones)
              </button>
              <button
                data-testid={`zones-toggle-${c.catalogue_no}`}
                title={disabled ? "Désactivé sur le site — cliquer pour activer" : "Actif sur le site — cliquer pour désactiver"}
                onClick={() => toggleCat(c.catalogue_no)}
                className={`px-2.5 py-2 rounded-full text-[10px] uppercase tracking-wider border transition-colors duration-300 ${disabled ? "border-red-400/40 text-red-300 hover:bg-red-400/10" : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"}`}>
                {disabled ? "Inactif" : "Actif"}
              </button>
            </span>
          );
        })}
      </div>

      {missingReport && missingReport.total > 0 && (
        <div className="mb-5 bg-red-500/10 border border-red-500/40 rounded-xl p-4" data-testid="zones-missing-report">
          <p className="text-xs text-red-400 font-semibold mb-2">
            {missingReport.total} zone(s) sans produit rattaché sur {missingReport.pages.length} page(s) —
            elles apparaissent en rouge (visible ici seulement, jamais sur le site) ; cliquez une page pour la corriger :
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {missingReport.pages.map((p) => (
              <button key={`${p.catalogue_no}-${p.page}`} data-testid={`zones-missing-${p.catalogue_no}-${p.page}`}
                onClick={() => jumpTo(p.catalogue_no, p.page)}
                className="px-2.5 py-1 rounded-full text-[11px] border border-red-400/40 text-red-300 hover:bg-red-400/15 transition-colors">
                nº {p.catalogue_no} p.{p.page} ({p.missing})
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="zones-prev" onClick={() => goPage(pageIdx - 1)} disabled={pageIdx <= 0}
          className="w-9 h-9 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold disabled:opacity-30 transition-colors duration-300">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm text-ivory/70">
          Page{" "}
          <input data-testid="zones-page-input" type="number" min={1} max={pageNums.length} value={pageNums[pageIdx] ?? ""}
            onChange={(e) => { const v = parseInt(e.target.value); const i = pageNums.indexOf(v); if (i >= 0) goPage(i); }}
            className="w-20 bg-ink border border-ivory/15 rounded-lg px-2 py-1 text-center text-sm focus:border-gold focus:outline-none mx-1" />
          {" "}/ {pageNums.length || "…"}
        </span>
        <button data-testid="zones-next" onClick={() => goPage(pageIdx + 1)} disabled={pageIdx >= pageNums.length - 1}
          className="w-9 h-9 rounded-full border border-ivory/20 flex items-center justify-center hover:border-gold disabled:opacity-30 transition-colors duration-300">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-xs text-ivory/65" data-testid="zones-count">{hotspots.length} zone(s) sur cette page</span>
        <div className="flex-1" />
        <button data-testid="zones-add" onClick={() => setDrawMode((d) => !d)}
          className={`px-4 py-2 rounded-full text-xs border flex items-center gap-2 transition-colors duration-300 ${drawMode ? "bg-gold text-ink border-gold font-semibold" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
          <BoxSelect className="w-4 h-4" /> {drawMode ? "Cliquez-glissez sur l'image…" : "Ajouter une zone"}
        </button>
        <button data-testid="zones-reset" onClick={() => loadPage(cat, pageNums[pageIdx])} disabled={!dirty}
          className="px-4 py-2 rounded-full text-xs border border-ivory/20 text-ivory/70 hover:border-gold/60 disabled:opacity-30 flex items-center gap-2 transition-colors duration-300">
          <RotateCcw className="w-3.5 h-3.5" /> Annuler
        </button>
        <button data-testid="zones-save" onClick={save} disabled={!dirty || saving}
          className="px-5 py-2 rounded-full text-xs bg-gold text-ink font-semibold hover:bg-champagne disabled:opacity-30 flex items-center gap-2 transition-colors duration-300">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
        <div ref={canvasRef} data-testid="zones-canvas" onMouseDown={onCanvasDown}
          className={`relative bg-[#f4f2ec] rounded-xl overflow-hidden select-none ${drawMode ? "cursor-crosshair" : ""}`}>
          {loading || !pageData ? (
            summaryLoaded && cats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 px-8 text-center" data-testid="zones-empty">
                <BoxSelect className="w-10 h-10 text-gold/50 mb-4" />
                <p className="text-ivory/70 font-medium mb-2">Aucune page de catalogue en base</p>
                <p className="text-sm text-ivory/70 max-w-md">Les pages des catalogues n'ont pas encore été importées sur ce site.
                  Utilisez « Restaurer depuis un ZIP » dans l'onglet Paramètres (carte Sauvegarde) pour importer les données.</p>
              </div>
            ) : (
              <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
            )
          ) : (
            <>
              <img src={pageData.image} alt={`Page ${pageData.page}`} className="w-full block pointer-events-none" draggable={false} />
              {hotspots.map((h, i) => (
                <div key={i} data-testid={`hotspot-box-${i}`}
                  onMouseDown={(e) => onHotspotDown(e, i, false)}
                  title={productMap[h.pid]?.name || h.pid || "Zone sans produit"}
                  className={`absolute border-2 cursor-move transition-colors duration-150 ${
                    sel === i ? "border-gold bg-gold/25 z-10" : h.pid ? "border-gold/60 bg-gold/10 hover:bg-gold/20" : "border-red-400 bg-red-400/15"
                  }`}
                  style={{
                    left: `${(h.x / pageData.width) * 100}%`, top: `${(h.y / pageData.height) * 100}%`,
                    width: `${(h.w / pageData.width) * 100}%`, height: `${(h.h / pageData.height) * 100}%`,
                  }}>
                  {sel === i && (
                    <div data-testid={`hotspot-handle-${i}`} onMouseDown={(e) => onHotspotDown(e, i, true)}
                      className="absolute -bottom-2 -right-2 w-4 h-4 bg-gold rounded-full border-2 border-ink cursor-nwse-resize" />
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="bg-surface border border-ivory/10 rounded-xl p-5 lg:sticky lg:top-28" data-testid="zones-panel">
          {selH ? (
            <>
              <p className="text-gold tracking-[0.2em] uppercase text-[11px] mb-3">Zone {sel + 1}</p>
              {selProd ? (
                <div className="flex items-center gap-3 mb-4">
                  {selProd.image && <img src={selProd.image} alt="" className="w-12 h-12 object-contain bg-[#f4f2ec] rounded" />}
                  <div>
                    <p className="text-sm font-medium" data-testid="zones-selected-name">{selProd.name}</p>
                    {selProd.sku && <p className="text-xs text-ivory/65">Réf. {selProd.sku}</p>}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-red-400 mb-4" data-testid="zones-no-product">
                  {selH.pid ? `Produit : ${selH.pid}` : "Aucun produit assigné — recherchez ci-dessous."}
                </p>
              )}
              {selProd && (
                <>
                  <button data-testid="zones-fiche-toggle" onClick={openFiche}
                    className={`w-full mb-4 px-4 py-2.5 rounded-full text-xs border flex items-center justify-center gap-2 transition-colors duration-300 ${ficheOpen ? "bg-gold text-ink border-gold font-semibold" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
                    <Pencil className="w-3.5 h-3.5" /> {ficheOpen ? "Fermer la fiche" : "Modifier la fiche du produit"}
                  </button>
                  {ficheOpen && fiche && (
                    <div className="border border-gold/25 rounded-lg p-3 mb-4 space-y-2.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="zones-fiche-form">
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Nom (FR)</label>
                        <input data-testid="zones-fiche-name" value={fiche.name}
                          onChange={(e) => setFiche((f) => ({ ...f, name: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Nom (EN)</label>
                        <input data-testid="zones-fiche-name-en" value={fiche.name_en}
                          onChange={(e) => setFiche((f) => ({ ...f, name_en: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] text-ivory/65 mb-1">Référence</label>
                          <input data-testid="zones-fiche-sku" value={fiche.sku}
                            onChange={(e) => setFiche((f) => ({ ...f, sku: e.target.value }))}
                            className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-[11px] text-ivory/65 mb-1">Prix de base ($)</label>
                          <input data-testid="zones-fiche-price" type="number" min="0.01" step="0.01" value={fiche.base_price}
                            onChange={(e) => setFiche((f) => ({ ...f, base_price: e.target.value }))}
                            className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Prix coûtant ($, optionnel — jamais affiché aux clients)</label>
                        <input data-testid="zones-fiche-cost" type="number" min="0" step="0.01" value={fiche.cost_price}
                          onChange={(e) => setFiche((f) => ({ ...f, cost_price: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Accroche</label>
                        <input data-testid="zones-fiche-tagline" value={fiche.tagline}
                          onChange={(e) => setFiche((f) => ({ ...f, tagline: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Description (FR)</label>
                        <textarea data-testid="zones-fiche-desc" rows={3} value={fiche.description}
                          onChange={(e) => setFiche((f) => ({ ...f, description: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] text-ivory/65 mb-1">Description (EN)</label>
                        <textarea data-testid="zones-fiche-desc-en" rows={2} value={fiche.description_en}
                          onChange={(e) => setFiche((f) => ({ ...f, description_en: e.target.value }))}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gold/90 mb-1">Grandeurs (étiquette + supplément $)</label>
                        {fiche.sizes.map((s, i) => (
                          <div className="flex gap-1.5 mb-1.5" key={i}>
                            <input data-testid={`zones-fiche-size-label-${i}`} value={s.label} placeholder="Ex. : 9 po"
                              onChange={(e) => updRow("sizes", i, "label", e.target.value)}
                              className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <input data-testid={`zones-fiche-size-extra-${i}`} type="number" min="0" step="0.01" value={s.extra} placeholder="+ $"
                              onChange={(e) => updRow("sizes", i, "extra", e.target.value)}
                              className="w-16 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <button data-testid={`zones-fiche-size-del-${i}`} onClick={() => delRow("sizes", i)}
                              className="text-ivory/40 hover:text-red-400 px-1 transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <button data-testid="zones-fiche-size-add" onClick={() => addRow("sizes", { id: "", label: "", extra: 0 })}
                          className="text-[11px] text-gold/80 hover:text-gold underline underline-offset-2 transition-colors">+ Ajouter une grandeur</button>
                      </div>
                      <div>
                        <label className="block text-[11px] text-gold/90 mb-1">Finitions / couleurs (étiquette + supplément $)</label>
                        {fiche.finishes.map((s, i) => (
                          <div className="flex gap-1.5 mb-1.5" key={i}>
                            <input data-testid={`zones-fiche-finish-label-${i}`} value={s.label} placeholder="Ex. : Or"
                              onChange={(e) => updRow("finishes", i, "label", e.target.value)}
                              className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <input data-testid={`zones-fiche-finish-extra-${i}`} type="number" min="0" step="0.01" value={s.extra} placeholder="+ $"
                              onChange={(e) => updRow("finishes", i, "extra", e.target.value)}
                              className="w-16 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <button data-testid={`zones-fiche-finish-del-${i}`} onClick={() => delRow("finishes", i)}
                              className="text-ivory/40 hover:text-red-400 px-1 transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <button data-testid="zones-fiche-finish-add" onClick={() => addRow("finishes", { id: "", label: "", extra: 0 })}
                          className="text-[11px] text-gold/80 hover:text-gold underline underline-offset-2 transition-colors">+ Ajouter une finition / couleur</button>
                      </div>
                      <div>
                        <label className="block text-[11px] text-gold/90 mb-1">Paliers de prix (quantité min. → prix unitaire $)</label>
                        {fiche.price_tiers.map((s, i) => (
                          <div className="flex gap-1.5 mb-1.5" key={i}>
                            <input data-testid={`zones-fiche-tier-qty-${i}`} type="number" min="1" value={s.min_qty} placeholder="Qté"
                              onChange={(e) => updRow("price_tiers", i, "min_qty", e.target.value)}
                              className="w-16 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <input data-testid={`zones-fiche-tier-price-${i}`} type="number" min="0.01" step="0.01" value={s.price} placeholder="Prix $"
                              onChange={(e) => updRow("price_tiers", i, "price", e.target.value)}
                              className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-2 py-1.5 text-xs focus:border-gold focus:outline-none" />
                            <button data-testid={`zones-fiche-tier-del-${i}`} onClick={() => delRow("price_tiers", i)}
                              className="text-ivory/40 hover:text-red-400 px-1 transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <button data-testid="zones-fiche-tier-add" onClick={() => addRow("price_tiers", { min_qty: "", price: "" })}
                          className="text-[11px] text-gold/80 hover:text-gold underline underline-offset-2 transition-colors">+ Ajouter un palier</button>
                      </div>
                      <p className="text-[10px] text-ivory/50">
                        Photos, stock, frais d'inscription et rabais de quantité : onglet « Produits ».
                      </p>
                      <button data-testid="zones-fiche-save" onClick={saveFiche} disabled={ficheSaving}
                        className="w-full px-4 py-2.5 rounded-full text-xs bg-gold text-ink font-semibold hover:bg-champagne disabled:opacity-50 flex items-center justify-center gap-2 transition-colors duration-300">
                        {ficheSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Enregistrer la fiche
                      </button>
                    </div>
                  )}
                </>
              )}
              <p className="text-xs text-ivory/70 mb-2">Produit lié (rechercher par nom ou référence) :</p>
              <input data-testid="zones-search" type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nom, référence, catégorie…"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none mb-2" />
              {results.length > 0 && (
                <div className="border border-ivory/10 rounded-lg overflow-hidden mb-3" data-testid="zones-results">
                  {results.map((p) => (
                    <button key={p.id} data-testid={`zones-result-${p.id}`} onClick={() => assign(p)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-gold/10 border-b border-ivory/5 last:border-0 transition-colors duration-150">
                      <span className="block text-ivory/90">{p.name}</span>
                      <span className="text-ivory/65">{p.sku || p.id}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-ivory/65 mb-4">
                Glissez la zone pour la déplacer, la pastille dorée pour la redimensionner.
              </p>
              <button data-testid="zones-delete" onClick={removeSel}
                className="w-full px-4 py-2.5 rounded-full text-xs border border-red-400/40 text-red-400 hover:bg-red-400/10 flex items-center justify-center gap-2 transition-colors duration-300">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer cette zone
              </button>
            </>
          ) : (
            <div className="text-sm text-ivory/70" data-testid="zones-panel-empty">
              <p className="mb-3">Cliquez une zone dorée pour la modifier.</p>
              <p className="text-xs text-ivory/65">« Ajouter une zone » puis cliquez-glissez sur l'image pour encadrer un produit. N'oubliez pas d'enregistrer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
